import { AddIcon } from '@chakra-ui/icons';
import { Box, Button, FormControl, FormErrorMessage, FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Radio, RadioGroup, Stack, VStack, useDisclosure } from '@chakra-ui/react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

const MinorSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    birthDate: Yup.string().required('Birth date is required'),
    gender: Yup.string().required('Gender is required'),
});

export default function AddMinor({ handleMinor }) {
    const { t } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const formik = useFormik({
        initialValues: {
            name: '',
            birthDate: '',
            gender: '',
        },
        validationSchema: MinorSchema,
        onSubmit: (values, { resetForm }) => {
            if (typeof handleMinor === 'function') {
                handleMinor({
                    name: values.name,
                    birthDate: values.birthDate,
                    gender: values.gender,
                });
            }
            resetForm();
            onClose();
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    // Calculate max date (must be under 17 years old)
    let today = new Date();
    let eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(today.getFullYear() - 17);
    let dateMax = today.toISOString().split('T')[0];
    let dateMin = eighteenYearsAgo.toISOString().split('T')[0];

    useEffect(() => {
        formik.resetForm();
    }, [isOpen]);

    return (
        <>
            <Box my={4}>
                <Button leftIcon={<AddIcon />} colorScheme='primary' size='lg' fontSize='sm' fontWeight={700} onClick={onOpen} width='100%' variant='outline'>
                    {t('add_kids')}
                </Button>
            </Box>

            <Modal isOpen={isOpen} onClose={handleClose} size='lg' isCentered>
                <ModalOverlay backdropFilter='blur(4px)' />
                <ModalContent mx={4}>
                    <ModalHeader fontSize='2xl' fontWeight={600}>
                        {t('add_kids_title')}
                    </ModalHeader>
                    <ModalCloseButton />

                    <form onSubmit={formik.handleSubmit}>
                        <ModalBody pb={6}>
                            <VStack spacing={4} align='stretch'>
                                {/* Child Name */}
                                <FormControl isRequired isInvalid={!!formik.errors.name && formik.touched.name}>
                                    <FormLabel htmlFor='name'>{t('children_name')}</FormLabel>
                                    <Input id='name' name='name' type='text' size='lg' placeholder='Enter child full name' value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
                                </FormControl>

                                {/* Birth Date */}
                                <FormControl isRequired isInvalid={!!formik.errors.birthDate && formik.touched.birthDate}>
                                    <FormLabel htmlFor='birthDate'>{t('birth_date')}</FormLabel>
                                    <Input id='birthDate' name='birthDate' type='date' size='lg' value={formik.values.birthDate} onChange={formik.handleChange} onBlur={formik.handleBlur} min={dateMin} max={dateMax} />
                                    <FormErrorMessage>{formik.errors.birthDate}</FormErrorMessage>
                                </FormControl>

                                {/* Gender */}
                                <FormControl isRequired isInvalid={!!formik.errors.gender && formik.touched.gender}>
                                    <FormLabel htmlFor='gender'>{t('gender')}</FormLabel>
                                    <RadioGroup id='gender' name='gender' value={formik.values.gender} onChange={(value) => formik.setFieldValue('gender', value)}>
                                        <Stack direction='row' spacing={5}>
                                            <Radio value='male' size='lg' colorScheme='primary'>
                                                Male
                                            </Radio>
                                            <Radio value='female' size='lg' colorScheme='primary'>
                                                Female
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>
                                    <FormErrorMessage>{formik.errors.gender}</FormErrorMessage>
                                </FormControl>
                            </VStack>
                        </ModalBody>

                        <ModalFooter>
                            <HStack spacing={3} w='full'>
                                <Button w='full' size='lg' variant='outline' colorScheme='gray' onClick={handleClose}>
                                    {t('cancel')}
                                </Button>
                                <Button w='full' size='lg' type='submit' colorScheme='primary'>
                                    {t('save')}
                                </Button>
                            </HStack>
                        </ModalFooter>
                    </form>
                </ModalContent>
            </Modal>
        </>
    );
}

AddMinor.propTypes = {
    handleMinor: PropTypes.func.isRequired,
};
